import React, { useState, useCallback, useEffect } from 'react';
import { RefreshCw, Wrench } from 'lucide-react';
import { Button } from './ui/button';
import { outagesAPI } from '../utils/api';
import JemDeviceMirrorPanel from './JemDeviceMirrorPanel';
import { toast } from 'sonner';

/* Admin data tools — grid backfill + summary recompute. Moved off the
   near-real-time Live Monitoring page. */
function OutageDataTools({ siteId, isAdmin }) {
  const [daysBack, setDaysBack] = useState(90);
  const [backfilling, setBackfilling] = useState(false);
  const [recomputing, setRecomputing] = useState(false);
  const [job, setJob] = useState(null);

  const refreshJob = useCallback(async () => {
    try {
      const res = await outagesAPI.getBackfillStatus(siteId);
      setJob(res.data);
    } catch (e) { console.debug('no backfill job yet', e); }
  }, [siteId]);

  useEffect(() => { refreshJob(); }, [refreshJob]);

  const handleBackfill = async () => {
    setBackfilling(true);
    try {
      const res = await outagesAPI.triggerBackfill(siteId, Number(daysBack) || 90);
      const chunks = res.data?.chunks_total || Math.ceil((Number(daysBack) || 90) / 7);
      toast.success(`Backfill started (${daysBack} days · ~${chunks} chunks). Runs in the background.`);
      const startedAt = Date.now();
      const poll = setInterval(async () => {
        try {
          const j = await outagesAPI.getBackfillStatus(siteId);
          setJob(j.data);
          if (j.data?.status && j.data.status !== 'in_progress') {
            clearInterval(poll);
            setBackfilling(false);
            toast.success(`Backfill complete · ${j.data.samples_inserted || 0} samples · ${j.data.events_new || 0} events`);
          } else if (Date.now() - startedAt > 3 * 60 * 1000) {
            clearInterval(poll);
            setBackfilling(false);
          }
        } catch (e) { console.debug('poll tick failed', e); }
      }, 3000);
    } catch (err) {
      setBackfilling(false);
      const d = err.response?.data?.detail;
      if (d && typeof d === 'object') toast.error(d.message || 'Backfill failed to start');
      else toast.error(typeof d === 'string' ? d : 'Backfill failed to start');
    }
  };

  const handleRecompute = async () => {
    setRecomputing(true);
    try {
      const res = await outagesAPI.recomputeSummaries(siteId);
      toast.success(`Recomputed: ${res.data?.events_processed || 0} events → ${res.data?.summaries_written || 0} months`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Recompute failed');
    } finally { setRecomputing(false); }
  };

  if (!isAdmin) return null;
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-sm p-4" data-testid="outage-admin-toolbox">
      <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-3 flex items-center gap-1.5">
        <Wrench className="w-3.5 h-3.5" /> Admin · data tools
      </h3>
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center space-x-2">
          <label className="text-[11px] text-slate-600">Grid backfill last</label>
          <input
            type="number" min="1" max="365" value={daysBack}
            onChange={(e) => setDaysBack(e.target.value)}
            className="w-20 border border-slate-300 rounded-sm px-2 py-1 text-sm"
            data-testid="backfill-days-input"
          />
          <span className="text-[11px] text-slate-600">days</span>
          <Button size="sm" onClick={handleBackfill} disabled={backfilling} data-testid="trigger-backfill-btn">
            {backfilling ? 'Running…' : 'Run backfill'}
          </Button>
        </div>
        <Button size="sm" variant="outline" onClick={handleRecompute} disabled={recomputing} data-testid="recompute-summaries-btn">
          <RefreshCw className={`w-3 h-3 mr-1 ${recomputing ? 'animate-spin' : ''}`} />
          Recompute summaries
        </Button>
        {job?.status && (
          <div className="text-[11px] text-slate-500" data-testid="backfill-job-status">
            Last job: <span className="font-mono">{job.status}</span>
            {job.samples_inserted != null && ` · ${job.samples_inserted} samples`}
            {job.events_new != null && ` · ${job.events_new} events`}
          </div>
        )}
      </div>
    </div>
  );
}

export default function SiteDiagnosticsTab({ siteId, isAdmin = false }) {
  return (
    <div className="space-y-6" data-testid="site-diagnostics-tab">
      <div className="text-[11px] text-slate-500">
        Device-level comms and payload diagnostics. Ingestion configuration is read/managed here —
        the Live Monitoring page only consumes what this pipeline produces.
      </div>
      <JemDeviceMirrorPanel siteId={siteId} isAdmin={isAdmin} />
      <OutageDataTools siteId={siteId} isAdmin={isAdmin} />
    </div>
  );
}
